import { KeyboardEvent, MouseEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button } from '@material-ui/core';

import { Icon, Menu, OptionItem } from '@components';

// Import Breadcrumbs component
import Breadcrumbs from '@components/Breadcrumbs';
import { content } from '@content';
import { textTemplate, useHistory } from '@utils';

import { TopBarProps } from './TopBar.props';
import { TopBarContext } from './TopBar.context';
import { useStyles } from './TopBar.styles';
import { MENU_OPTIONS, MenuKeys, TOP_BAR_CTX_VALUE } from './TopBar.const';
import { ViewportSizeContext, ViewportSize } from '../Viewport';
import {
  businessUnitDeployments,
  deployment,
  // enterprise,
  template,
  userClients,
} from '@modules';
import { useParams } from '@utils/searchQuery/searchQuery';
import { useAppDispatch } from '@store';
import { ChangeTemplateModal } from './ChangeTemplateModal';
import { updateTemplateExperienceIds } from '@routes/Deployments/Deployments.helpers';
import { ToggleButtons, ToggleButtonsProps } from '@components/ToggleButtons';
import { image } from '@content';

// SearchFilterSortUpload imports
import { useForm, Controller } from 'react-hook-form';
import { TextFieldProps } from '@components/TextField/TextField.props';
import { SearchFilterSortUpload } from '@components/SearchFilterSortUpload';

import { dataVisualization, imageLibrary, modules, ModulesSearchParams } from '@modules';

import { ITEMS_PER_PAGE_FOR_SUGGESTIONS, SUGGESTIONS_SEARCH_DEBOUNCE_DELAY_MS } from './TopBar.const';

import {
  FilterByOptions,
  LibrarySearchType,
  SearchFormProps,
  SortByOptions,
} from '@components/SearchFilterSortUpload/SearchFilterSortUpload.props';
import {
  FilterByValues,
  OptionsTypes,
  SortByValues,
} from '@components/SearchFilterSortUpload/SearchFilterSortUpload.const';
import useDebounceLast from '@utils/hooks/useDebounce';

/**
 * TopBar component
 * @param onDeploymentChange - action on change deployment in picker
 * @param onBackClick - action on click back button. 'breadcrumbs' view only
 * @param onClientChange - action on change businessUnit in picker
 * @returns {JSX.Element}
 */
export const TopBar = ({
  onDeploymentChange,
  onExperienceChange,
  onBackClick,
  onClientChange,
  onMailFileChange,
  onTemplateChange,
}: TopBarProps): JSX.Element => {
  const styles = useStyles();

  // const {
  //   breadcrumbs,
  //   sectionName,
  //   // moduleType,
  //   // moduleName,
  //   // chosenTreatment,
  //   // contentModuleName,
  //   // editableImageName,
  //   // moduleDeployment,
  //   // currentEmailTemplate,
  //   lastEmailTemplateId,
  //   isTemplateIdChanged,
  //   isTemplateListVisible,
  //   // experiences,

  //   // SearchFilterSortUpload context variables
  //   showSearchFilterSortUpload,
  //   contentCenterType,
  //   searchTerm,
  //   setIsDVEditorVisible,
  //   setIsImageEditorVisible,
  //   setIsTemplateEditorVisible,
  //   onSearch,
  //   onUploadModule,
  // } = {
  //   ...TOP_BAR_CTX_VALUE,
  //   ...useContext(TopBarContext)[0],
  // };

  const {
    breadcrumbs,
    sectionName,
    lastEmailTemplateId,
    isTemplateIdChanged,
    isTemplateListVisible,

    // SearchFilterSortUpload context variables
    showSearchFilterSortUpload,
    contentCenterType,
    searchTerm,
    setIsDVEditorVisible,
    setIsImageEditorVisible,
    setIsTemplateEditorVisible,
    onSearch,
    onUploadModule,
  } = useContext(TopBarContext)[0];

  const [, setTopBarCtx] = useContext(TopBarContext);
  const dispatch = useAppDispatch();
  // const [curbusinessUnitId, curEnterpriseId] = useParams('businessUnit', 'enterprise');
  const [curbusinessUnitId] = useParams('businessUnit');
  const history = useHistory();
  // const enterprises = enterprise.useListData();
  // const curEnterprise = enterprises.find((enterpriseItem) => enterpriseItem.id === Number(curEnterpriseId));
  const curDeployment = deployment.useDeploymentData();
  // const deployments = businessUnitDeployments.useData();
  // const businessUnit = curEnterprise?.businessUnits.find(
  //   (businessUnitItem) => businessUnitItem.id === Number(curbusinessUnitId),
  // );

  const [currentSection, setCurrentSection] = useState<string | undefined>(sectionName);

  const [resetViewportSizeToggle, setResetViewportSizeToggle] = useState(true);

  const [isChangeTemplateVisible, setIsChangeTemplateVisible] = useState(false);
  const { deployment: deploymentId, mailFile: mailFileId, template: templateId } = history.query;
  const currentClientData = userClients.useEntityData();
  const {
    data: { deploymentEmailTemplates },
  } = deployment.useInfoEmailTemplate();
  const isSaveTemplateDisabled = lastEmailTemplateId === templateId && !isTemplateIdChanged;

  const { viewportSize, setViewportSize, isViewportSizeEnabled, setIsViewportSizeEnabled } =
    useContext(ViewportSizeContext);

  /*****************************************************
   * SearchFilterSortUpload constants and useStates
  /*****************************************************/
  // constants and useStates for SearchFilterSortUpload
  const businessUnitId = Number(curbusinessUnitId);

  const { control, setValue, getValues } = useForm<SearchFormProps>({
    defaultValues: {
      searchType: 'deployment',
      keyword: searchTerm,
      filterBy: FilterByValues.NONE,
      sortBy: SortByValues.UPDATED,
    },
  });

  const [nameSuggestions, setNameSuggestions] = useState<TextFieldProps['suggestions']>([]);
  const [previousSearchType, setPreviousSearchType] = useState<LibrarySearchType>('deployment');
  const [resetSearchTerm, setResetSearchTerm] = useState(false);

  /*****************************************************
   * END: SearchFilterSortUpload constants and useStates
  /*****************************************************/

  /*****************************************************
   * SearchFilterSortUpload related code
  /*****************************************************/
  // setting name suggestions by businessUnit id and entered keyword
  const fetchSuggestions = useCallback(async () => {
    const query = {
      itemsPerPage: ITEMS_PER_PAGE_FOR_SUGGESTIONS,
      ...getValues(),
      businessUnitId,
      page: 1,
    } as ModulesSearchParams;

    const searchKeyword = query.keyword ?? '';
    let result: any;

    if (query.searchType === 'dataVisual') {
      dispatch(dataVisualization.actions.resetDataVisualizationList());
      result = await dispatch(
        dataVisualization.thunk.getListDataVisualization({
          ...query,
          businessUnit: query.businessUnitId,
          keyword: searchKeyword,
        }),
      );
    } else if (query.searchType === 'deployment') {
      result = await dispatch(modules.thunk.search(query));
    } else {
      // must be the image library
      result = await dispatch(
        imageLibrary.thunk.search({
          ...query,
          itemsPerPage: query.keyword ? ITEMS_PER_PAGE_FOR_SUGGESTIONS : 20,
          businessUnit: query.businessUnitId,
          keyword: searchKeyword,
          sortBy: 'id',
        } as any),
      );
    }

    setNameSuggestions(
      result.payload?.items.map((item: any) => {
        // return the template name if it's a deployment search, otherwise return the image name
        if (query.searchType === 'deployment') {
          return item.templateName;
        }
        return item.name;
      }),
    );
  }, [setNameSuggestions, businessUnitId, dispatch, getValues]);

  const [debouncedSearch] = useDebounceLast(async () => {
    await fetchSuggestions();
  }, SUGGESTIONS_SEARCH_DEBOUNCE_DELAY_MS);

  const handleChangeKeyword: TextFieldProps['onChange'] = useCallback(
    (value: string, suggested?: boolean | undefined) => {
      setValue('keyword', value);

      setTopBarCtx((prev) => ({
        ...prev,
        searchTerm: value,
      }));
      // dispatch(imageLibrary.actions.setSearch(getValues().keyword));

      // if (suggested || value.length < 2) {
      //   setNameSuggestions([]);
      //   fetchSuggestions();
      // } else {
      //   fetchSuggestions();
      // }
      setNameSuggestions([]);
      debouncedSearch();

      onSearch(getValues());
      // if (suggested) {
      //   onSearch(getValues());
      // }
    },
    [setValue, setTopBarCtx, debouncedSearch, onSearch, getValues],
  );

  const handleSearchEnter = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onSearch(getValues());
      }
    },
    [onSearch, getValues],
  );

  const handleFilterSortChange = useCallback(
    (item: OptionItem) => {
      switch (item.type) {
        case OptionsTypes.FILTER_BY:
          // handle filter by
          setValue('filterBy', item.value as FilterByOptions);
          break;
        case OptionsTypes.SORT_BY:
          // handle sort by
          setValue('sortBy', item.value as SortByOptions);
          break;
      }

      onSearch(getValues());
    },
    [setValue, onSearch, getValues],
  );

  const handleSearchTypeChange = useCallback(
    (event: MouseEvent<HTMLElement>, newType: LibrarySearchType) => {
      let searchType = newType;

      if (previousSearchType !== newType) {
        setResetSearchTerm(true);
      }

      // check if the newType passed a non-empty value, otherwise use the previous search type
      if (searchType) {
        setPreviousSearchType(searchType);
      } else {
        searchType = previousSearchType;
      }

      switch (searchType) {
        case 'deployment':
          setIsImageEditorVisible(false);
          setIsTemplateEditorVisible(false);
          setIsDVEditorVisible(false);
          setValue('keyword', '');
          break;
        case 'module':
          setIsImageEditorVisible(false);
          setIsTemplateEditorVisible(false);
          setIsDVEditorVisible(false);
          setValue('keyword', '');
          break;
        case 'library':
          setIsTemplateEditorVisible(false);
          setIsImageEditorVisible(false);
          setIsDVEditorVisible(false);
          setValue('keyword', '');
          break;
        case 'dataVisual':
          setIsImageEditorVisible(false);
          setIsTemplateEditorVisible(false);
          setIsDVEditorVisible(false);
          setValue('keyword', '');
          break;
        default:
          break;
      }

      if (searchType) {
        setValue('searchType', searchType);
      }

      onSearch(getValues());
    },
    [
      onSearch,
      getValues,
      previousSearchType,
      setIsImageEditorVisible,
      setIsTemplateEditorVisible,
      setIsDVEditorVisible,
      setValue,
    ],
  );

  // on contentCenterType change, reset the search term
  useEffect(() => {
    switch (contentCenterType) {
      case 'contentModuleLibrary':
        setValue('searchType', 'deployment');
        break;
      case 'imageLibrary':
        setValue('searchType', 'library');
        break;
      case 'dataVisualizationLibrary':
        setValue('searchType', 'dataVisual');
        break;
      default:
        break;
    }

    setValue('keyword', '');

    setTopBarCtx((prev) => ({
      ...prev,
      searchTerm: '',
    }));

    // reset suggestion list and search term on content center type change
    setNameSuggestions([]);
    setResetSearchTerm(true);
  }, [contentCenterType, setTopBarCtx, setValue]);

  // reset imageLibrary search on unmount
  useEffect(() => {
    // setTopBarCtx((prev) => ({
    //   ...prev,
    //   moduleType: 'Tab',
    //   moduleName: content.contentModules,
    // }));

    return () => {
      // setTopBarCtx(() => TOP_BAR_CTX_VALUE);
      dispatch(imageLibrary.actions.setSearch(''));
    };
  }, [dispatch]);

  useEffect(() => {
    if (getValues('keyword') !== searchTerm) {
      setValue('keyword', searchTerm);
    }
  }, [getValues, searchTerm, setValue]);
  /*****************************************************
   * END: SearchFilterSortUpload related code
  /*****************************************************/

  /*****************************************************
   * Breadcrumbs related code
  /*****************************************************/
  // create breadcrumbs if in the deployment section
  useEffect(() => {
    if (sectionName && sectionName === textTemplate(content.entityManager, { entity: content.deployment })) {
      const newBreadcrumbs: string[] = [];
      newBreadcrumbs.push(sectionName);

      if (curDeployment?.program.name) {
        newBreadcrumbs.push(curDeployment.program.name);

        if (curDeployment?.name) {
          newBreadcrumbs.push(curDeployment.name);

          // if (moduleName) {
          //   newBreadcrumbs.push(moduleName);

          //   if (chosenTreatment?.name) {
          //     newBreadcrumbs.push(chosenTreatment.name);
          //   }
          // }
        }
      }

      setTopBarCtx((prev) => ({
        ...prev,
        breadcrumbs: newBreadcrumbs,
      }));
    }
  }, [
    // chosenTreatment,
    // chosenTreatment?.name,
    // contentModuleName,
    curDeployment,
    // currentEmailTemplate,
    // editableImageName,
    // moduleDeployment,
    // moduleName,
    // moduleType,
    sectionName,
    setTopBarCtx,
  ]);

  // clear the breadcrumbs if the user is not in the deployment manager or content manager
  useEffect(() => {
    if (
      currentSection !== textTemplate(content.entityManager, { entity: content.deployment }) &&
      currentSection !== textTemplate(content.entityManager, { entity: content.content })
    ) {
      setTopBarCtx((prev) => ({
        ...prev,
        breadcrumbs: [],
      }));
    }
  }, [currentSection, setTopBarCtx]);

  /*****************************************************
   * END: Breadcrumbs related code
  /*****************************************************/

  // used by the viewport size toggle to set the size on change
  const handleViewportSizeChange = useCallback(
    (e: React.MouseEvent | null, size: string) => {
      if (e && typeof e.stopPropagation === 'function') {
        e.stopPropagation();
      }

      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }

      setViewportSize(size as ViewportSize);
    },
    [setViewportSize],
  );

  // Properties for the viewport size toggle
  const viewportToggleButtonsProps: ToggleButtonsProps = {
    id: 'viewport-size-toggle',
    ariaLabel: 'Viewport Toggle',
    defaultValue: viewportSize,
    isEnabled: isViewportSizeEnabled,
    onChangeHandler: handleViewportSizeChange,
    reset: resetViewportSizeToggle,
    toggleButtons: [
      {
        value: ViewportSize.Desktop,
        label: content.desktop,
        altText: content.desktop,
        ariaLabel: content.desktop,
        // iconSVG: <Icon.Desktop width="24px" height="24px" />,
      },
      {
        value: ViewportSize.Mobile,
        label: content.mobile,
        altText: content.mobile,
        ariaLabel: content.mobile,
        // iconSVG: <Icon.Mobile width="24px" height="24px" />,
      },
    ],
  };

  //const [viewportToggleButtonsProps, setViewportToggleButtonsProps] = useState(viewportToggleButtonsPropsDefaults);

  // Properties for the Preview toggle
  // const previewToggleButtonsProps: ToggleButtonsProps = {
  //   ariaLabel: 'Preview Toggle',
  //   defaultValue: 'editor',
  //   isEnabled: false,
  //   onChangeHandler: handleViewportSizeChange,
  //   toggleButtons: [
  //     {
  //       value: 'editor',
  //       altText: content.editor,
  //       ariaLabel: content.editor,
  //       iconSVG: <Icon.EditTemplate width="20px" height="20px" />,
  //     },
  //     {
  //       value: 'preview',
  //       altText: content.preview,
  //       ariaLabel: content.preview,
  //       iconSVG: <Icon.Preview width="24px" height="24px" />,
  //     },
  //   ],
  // };

  useEffect(() => {
    dispatch(businessUnitDeployments.thunk.getAll(Number(curbusinessUnitId)));
  }, [curbusinessUnitId, dispatch]);

  const onMenuClick = useCallback(
    (option: OptionItem) => {
      const moduleModalClose = document.getElementById('module-modal-close');

      moduleModalClose?.click();

      switch (option.id) {
        case MenuKeys.CHANGE_EMAIL_TEMPLATE:
          setTopBarCtx((prev) => ({
            ...prev,
            isTemplateListVisible: true,
          }));
          break;
        case MenuKeys.MANAGE_DEPLOYMENT:
          setTopBarCtx((prev) => ({
            ...prev,
            isTemplateListVisible: false,
          }));
          break;
      }
    },
    [setTopBarCtx],
  );

  // let breadCrumbsArray: { label: string; value: string | undefined }[] = [];
  // let breadCrumbsArray = [
  //   {
  //     label: 'Enterprise',
  //     value: curEnterprise?.name,
  //   },
  //   {
  //     label: 'Business Unit',
  //     value: businessUnit?.name,
  //   },
  // ];

  // if (sectionName) {
  //   breadCrumbsArray = breadCrumbsArray.concat([
  //     {
  //       label: 'Tool',
  //       value: sectionName,
  //     },
  //   ]);
  // }

  // if (curDeployment && sectionName === textTemplate(content.entityManager, { entity: content.deployment })) {
  //   breadCrumbsArray = breadCrumbsArray.concat([
  //     {
  //       label: 'Program',
  //       value: curDeployment?.program.name,
  //     },
  //     {
  //       label: 'Deployment',
  //       value: curDeployment?.name,
  //     },
  //   ]);
  // }

  // if (moduleDeployment && sectionName === textTemplate(content.entityManager, { entity: content.content })) {
  //   const curModuleDeployment = deployments.find((dep) => dep.name === moduleDeployment);

  //   breadCrumbsArray = breadCrumbsArray.concat([
  //     {
  //       label: 'Program',
  //       value: curModuleDeployment?.program.name,
  //     },
  //     {
  //       label: 'Deployment',
  //       value: moduleDeployment,
  //     },
  //   ]);
  // }

  // if (
  //   curDeployment &&
  //   sectionName === textTemplate(content.entityManager, { entity: content.deployment }) &&
  //   history.query.template
  // ) {
  //   breadCrumbsArray = breadCrumbsArray.concat([
  //     {
  //       label: 'Template',
  //       value: curDeployment?.currentEmailTemplate?.name,
  //     },
  //   ]);
  // }

  // if (currentEmailTemplate && sectionName === textTemplate(content.entityManager, { entity: content.content })) {
  //   breadCrumbsArray = breadCrumbsArray.concat([
  //     {
  //       label: 'Template',
  //       value: currentEmailTemplate,
  //     },
  //   ]);
  // }

  // if (moduleType && moduleType !== content.singleModule && moduleName) {
  //   breadCrumbsArray.push({
  //     label: `${moduleType}`,
  //     value: moduleName,
  //   });
  // }

  // if (editableImageName) {
  //   breadCrumbsArray = breadCrumbsArray.concat([
  //     {
  //       label: 'Edit image',
  //       value: editableImageName,
  //     },
  //   ]);
  // }

  // if (chosenTreatment) {
  //   breadCrumbsArray.push({
  //     label: 'Segment',
  //     value: chosenTreatment.name,
  //   });
  // }

  // if (contentModuleName) {
  //   breadCrumbsArray.push({
  //     label: 'Content Module',
  //     value: contentModuleName,
  //   });
  // }

  const handleShowChangeTemplate = () => {
    setIsChangeTemplateVisible(true);
  };

  const handleChangeTemplateClose = async () => {
    if (lastEmailTemplateId) {
      const firstMailFile = currentClientData?.mailFile[0];

      const selectionResult = await dispatch(template.thunk.selectTemplateToDeployment(lastEmailTemplateId));

      if (template.thunk.selectTemplateToDeployment.fulfilled.match(selectionResult)) {
        const tmplUpdated = await updateTemplateExperienceIds(lastEmailTemplateId, dispatch, deploymentEmailTemplates);

        if (tmplUpdated) {
          history.push('', { template: lastEmailTemplateId });
        }
      }

      if (deploymentId && !mailFileId && firstMailFile) {
        dispatch(deployment.thunk.attachMailFile({ deploymentId, mailFileId: firstMailFile.id }));
      }
      setIsChangeTemplateVisible(false);

      setTopBarCtx((prev) => ({
        ...prev,
        isTemplateIdChanged: false,
        isTemplateListVisible: false,
      }));
    }
  };

  const handleCancelChangeTemplate = () => {
    handleChangeTemplateClose();
  };

  const handleChangeTemplateConfirm = () => {
    setIsChangeTemplateVisible(false);
    setTopBarCtx((prev) => ({
      ...prev,
      isTemplateListVisible: false,
      isTemplateIdChanged: false,
    }));
  };

  // check if the section has changed. If so, reset the viewport size
  useEffect(() => {
    if (currentSection && currentSection !== sectionName) {
      setCurrentSection(sectionName);
      setResetViewportSizeToggle(true);
      handleViewportSizeChange(null, viewportToggleButtonsProps.defaultValue);
    }
  }, [currentSection, handleViewportSizeChange, sectionName, viewportToggleButtonsProps.defaultValue]);

  return (
    <>
      <Box className={styles.topBar}>
        <Box className={styles.logoContainer}>
          <img src={image.pilotlaunchaiLogoGraphite} alt={content.company} className={styles.logoPilotLaunchAI} />
        </Box>

        {/* {breadCrumbsArray.length > 0 && (
        <Breadcrumbs separator={<Icon.ChevronRight />} classes={{ root: styles.crumbs }}>
          {breadCrumbsArray.map((crumb, index) => {
            const last = index === breadCrumbsArray.length - 1;

            return (
              <Box key={`crumb-${index}`} className={styles.crumbsActive}>
                <Typography classes={{ root: styles.crumbsLabel }}>{crumb.label}</Typography>
                <Typography classes={{ root: styles.crumbsValue }}>{crumb.value}</Typography>
              </Box>
            );
          })}
        </Breadcrumbs>
      )} */}
        {showSearchFilterSortUpload && (
          <SearchFilterSortUpload
            className={styles.searchFilterSortUploadContainer}
            searchBarClassName={styles.searchBar}
            control={control}
            placeholder={content.search}
            resetSearchTerm={resetSearchTerm}
            showFilterSort={['contentModuleLibrary', 'templates'].includes(contentCenterType)}
            showUpload={['contentModuleLibrary', 'imageLibrary', 'dataVisualizationLibrary'].includes(
              contentCenterType,
            )}
            suggestions={nameSuggestions}
            onFilterSortChange={handleFilterSortChange}
            onKeyUp={handleSearchEnter}
            onSearchTermChange={handleChangeKeyword ?? (() => {})}
            onUploadClick={onUploadModule}
            setResetSearchTerm={setResetSearchTerm}
            setSearchValue={setValue}
          />
        )}
        {sectionName === textTemplate(content.entityManager, { entity: content.deployment }) && (
          <Box className={styles.actions}>
            {/* {!isTemplateListVisible && !isSubjectLinePreheaderEditMode && ( */}
            {!isTemplateListVisible && (
              <Box className={styles.buttonContainer}>
                <ToggleButtons {...viewportToggleButtonsProps} />
                <Menu
                  buttonClassName={styles.menu}
                  onClick={onMenuClick}
                  options={MENU_OPTIONS}
                  icon={<Icon.ArrowDown height="20" width="20" />}
                  disableRipple
                />
              </Box>
            )}

            {isTemplateListVisible && (
              // Save Selected Template button
              <Box className={styles.templateListButtonContainer}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isSaveTemplateDisabled}
                  onClick={handleShowChangeTemplate}
                  className={styles.saveButton}
                >
                  {content.save}
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCancelChangeTemplate}
                  className={styles.cancelButton}
                >
                  {content.cancel}
                </Button>
              </Box>
            )}

            {/* <ToggleButtons {...previewToggleButtonsProps} /> */}
          </Box>
        )}

        {isChangeTemplateVisible && (
          <ChangeTemplateModal
            title="Are you sure you want to change the template?"
            submitLabel="Confirm changes"
            cancelLabel="Discard changes"
            onClose={handleChangeTemplateClose}
            onSubmit={handleChangeTemplateConfirm}
          />
        )}
      </Box>
      {/* Breadcrumbs integration */}
      <Breadcrumbs breadcrumbs={breadcrumbs} className={styles.breadcrumbsContainer} />
    </>
  );
};
